import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { downloadURI } from '../../utils/downloadURI';
import { useFrontendColorCorrection } from '../openCV/hooks/useFrontendColorCorrection';

interface RequestedDownload {
  cdnURI: string;
  fileName: string;
}

/**
 * Hook to handle downloading the original image with color correction applied.
 */
export function useOriginalImageDownloader() {
  const { t } = useTranslation();
  const [originalImage, setOriginalImage] = useState<HTMLImageElement | null>(
    null
  );
  const [requestedDownload, setRequestedDownload] =
    useState<RequestedDownload | null>(null);

  // Pass the originalImage into useFrontendColorCorrection to get the corrected version
  const { finalImage, retriesExceeded } = useFrontendColorCorrection(
    requestedDownload ? true : false,
    originalImage,
    [1024]
  );

  useEffect(() => {
    if (retriesExceeded) {
      const message = t(
        'Color correction is unavailable on this device, the original image will be downloaded instead and may appear dark.'
      );
      console.error(message);
      alert(message);
    }
  }, [retriesExceeded, t]);

  /**
   * Fetches the original image from the CDN and sets it as `originalImage`.
   */
  const fetchAndSetOriginalImage = useCallback(async (cdnURI: string) => {
    try {
      const response = await fetch(cdnURI);
      if (!response.ok) {
        throw new Error('Failed to fetch the original image.');
      }
      const blob = await response.blob();

      const img = new Image();
      img.crossOrigin = 'Anonymous'; // handle CORS if needed
      const objectURL = URL.createObjectURL(blob);
      img.src = objectURL;

      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          resolve();
        };
        img.onerror = () => {
          reject(new Error('Failed to load image from blob'));
        };
      });

      // Once loaded, set the image
      setOriginalImage(img);

      // Clean up the object URL after setting the image
      URL.revokeObjectURL(objectURL);
    } catch (error) {
      console.error('Error fetching original image:', error);
      setOriginalImage(null);
    }
  }, []);

  /**
   * This is the function you call when the user clicks the "download original corrected image" button.
   * It sets up the requested download state and triggers fetching the original image.
   */
  const downloadOriginalCorrectedImage = useCallback(
    (cdnURI: string, fileName: string) => {
      setRequestedDownload({ cdnURI, fileName });
      // We'll fetch the image after setting this state in a useEffect
    },
    []
  );

  // When requestedDownload changes, fetch the image
  useEffect(() => {
    if (requestedDownload) {
      // Fetch the original image
      fetchAndSetOriginalImage(requestedDownload.cdnURI);
    }
  }, [requestedDownload, fetchAndSetOriginalImage]);

  // Once finalImage is available and we have a requestedDownload, we can download it
  useEffect(() => {
    if (finalImage && requestedDownload) {
      // finalImage is an HTMLImageElement
      // Convert finalImage to a data URL and download
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = finalImage.width;
      tempCanvas.height = finalImage.height;
      const ctx = tempCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(finalImage, 0, 0);
        const dataURL = tempCanvas.toDataURL('image/png');
        downloadURI(dataURL, requestedDownload.fileName);
      } else {
        console.error(
          'Unable to get canvas context for downloading corrected image.'
        );
      }

      // Reset states so we can handle future downloads if needed
      setRequestedDownload(null);
      setOriginalImage(null);
    }
  }, [finalImage, requestedDownload]);

  return {
    downloadOriginalCorrectedImage,
  };
}
